import { useDispatch } from "react-redux";
import { forwardRef, ReactNode, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { clearFilters } from "redux/SingleDataset/actions";
import { AMP_DATASET_EXPLORATION_EVENT__TAB_CHANGED } from "helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__TAB_NAME } from "helpers/constants/amplitudeProperties";
import { TabTypes } from "types";
import ExploreTabIcon from "assets/icons/ExploreTabIcon";
import EnrichmentIcon from "assets/icons/EnrichmentIcon";
import DataTabIcon from "assets/icons/DataTabIcon";
import classNames from "classnames";
import styles from "./style.module.scss";
import VLTooltip from "views/uikit/VLTooltip";
import { TrendUpIcon } from "../../../../assets/icons/TrendUpIcon";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import {
  ADD_MEDIA_TAB,
  ENRICHMENT_ENRICH_DATASET,
} from "../../../../helpers/constants/filters";
import { useUserPermission } from "../../../../hooks/useUserPermission";
import { NO_PERMISSION_TOOLTIP_TEXT } from "../../../../helpers/constants/tooltips";

interface TabProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  showBeta?: boolean;
  disabled: boolean;
  className?: string;
}

interface TabSwitchProps {
  showTabs: TabTypes[];
  dataset: any;
  className?: string;
}

export const Tab = forwardRef<HTMLDivElement, TabProps>(function Tab(
  { icon, label, onClick, disabled, selected, className, ...rest },
  ref,
) {
  const tabButtonContainer = classNames(
    styles.tabButton,
    {
      [styles.selected]: selected,
      [styles.disableTab]: disabled,
    },
    className,
  );

  return (
    <div
      ref={ref}
      className={tabButtonContainer}
      onClick={disabled ? undefined : onClick}
      {...rest}
    >
      {icon}
      {label}
      {selected && (
        <div
          className={classNames(
            styles.selectedBorderBottom,
            "selectedBorderBottom",
          )}
        ></div>
      )}
    </div>
  );
});

type TabData = Pick<TabProps, "label" | "disabled" | "icon"> & {
  tooltip?: string;
  type: TabTypes;
};

const TabSwitch = ({ showTabs, dataset, className }: TabSwitchProps) => {
  const { datasetId, newDatasetId, enrichDatasetId, viewsDatasetId } =
    useParams();

  const initialTab = enrichDatasetId
    ? TabTypes.DATA_ENRICHMENT
    : newDatasetId
      ? TabTypes.DATA_INGESTION
      : viewsDatasetId
        ? TabTypes.SAVED_VIEWS
        : TabTypes.DATA_EXPLORATION;

  const [selectedTab, setSelectedTab] = useState<TabTypes>(initialTab); //does not contain the BOTH value

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handlePageClick = (value: TabTypes) => {
    if (selectedTab === value) return;
    const id = newDatasetId || enrichDatasetId || datasetId || viewsDatasetId;
    setSelectedTab(value);
    switch (value) {
      case TabTypes.DATA_EXPLORATION:
        navigate(`/dataset/${id}/data`);
        break;

      case TabTypes.DATA_INGESTION:
        dispatch(clearFilters() as any);
        const batchId = dataset?.current_batch_id;
        if (!dataset.creating) {
          navigate(`/dataset/${id}/update${batchId ? `/${batchId}` : ""}`);
        } else {
          navigate(`/dataset/${id}/create`);
        }
        break;

      case TabTypes.DATA_ENRICHMENT:
        dispatch(clearFilters() as any);
        navigate(`/dataset/${id}/enrich`);
        break;

      case TabTypes.SAVED_VIEWS:
        dispatch(clearFilters() as any);
        navigate(`/dataset/${id}/views`);
        break;
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__TAB_CHANGED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__TAB_NAME]: value,
    });
  };

  const canAddMedia = useUserPermission("can_add_media");
  const { enabled: addMediaTabEnabled, reason: addMediaTabReason } =
    useFeatureFlagService(ADD_MEDIA_TAB);

  const disabledDataTab = !canAddMedia || !addMediaTabEnabled;

  const { enabled: enrichmentTabEnabled, reason: enrichmentTabReason } =
    useFeatureFlagService(ENRICHMENT_ENRICH_DATASET);

  const canEnrich = useUserPermission("can_enrich");

  const tabs = useMemo<TabData[]>(() => {
    const isEnrichmentDisabled = !enrichmentTabEnabled || !canEnrich;
    return [
      {
        type: TabTypes.DATA_INGESTION,
        label: "Data",
        icon: <DataTabIcon />,
        disabled: disabledDataTab,
        tooltip:
          addMediaTabReason ||
          (!canAddMedia ? NO_PERMISSION_TOOLTIP_TEXT : null),
      },
      {
        type: TabTypes.SAVED_VIEWS,
        label: "Views",
        icon: <TrendUpIcon />,
      },
      {
        type: TabTypes.DATA_EXPLORATION,
        label: "Explore",
        icon: <ExploreTabIcon />,
      },
      {
        type: TabTypes.DATA_ENRICHMENT,
        label: "Enrich",
        icon: <EnrichmentIcon />,
        disabled: isEnrichmentDisabled,
        tooltip:
          enrichmentTabReason ||
          (!canEnrich ? NO_PERMISSION_TOOLTIP_TEXT : undefined),
      },
    ]
      .filter((tab) => tab && showTabs.includes(tab.type))
      .map((tab) => tab as TabData);
  }, [
    enrichmentTabEnabled,
    canEnrich,
    disabledDataTab,
    addMediaTabReason,
    canAddMedia,
    enrichmentTabReason,
    showTabs,
  ]);

  const filteredTabs = tabs.filter((tab) => showTabs.includes(tab.type));

  return (
    <div className={classNames(styles.pageSwitchContainer, className)}>
      {filteredTabs.map((tab) => {
        const selected = selectedTab === tab.type;
        return (
          <VLTooltip
            key={tab.type}
            placement={"bottom"}
            title={tab.disabled ? tab.tooltip : null}
          >
            <Tab
              label={tab.label}
              selected={selected}
              disabled={tab.disabled}
              onClick={() => handlePageClick(tab.type)}
              icon={tab.icon}
            />
          </VLTooltip>
        );
      })}
    </div>
  );
};

export default TabSwitch;
